import React, {useEffect, useState} from 'react';
import clinicService from '../../../../../services/clinic.service';
import {Collapse, Pagination, Table, Alert} from 'react-bootstrap';
import Loading from "../../../../common/Loading";

function FeedbackTable({clinicid}) {
    const [feedbacks, setFeedbacks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [copiedItems, setCopiedItems] = useState({});

    const [pagination, setPagination] = useState({
        page: 0,
        size: 5,
        totalElements: 0,
        totalPages: 0
    });

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const getFeedbacks = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const res = await clinicService.getReputationFeedbacks(
                    clinicid,
                    pagination.page,
                    pagination.size,
                    {signal: controller.signal}
                );

                if (isMounted) {
                    setFeedbacks(res.data.content);
                    setPagination(prev => ({
                        ...prev,
                        totalElements: res.data.totalElements,
                        totalPages: res.data.totalPages
                    }));

                    if (res.data.content.length === 0) {
                        setError('No feedbacks found.');
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError' && isMounted) {
                    console.error("Fetch error:", err);
                    setError('Unable to load feedbacks. Please try again.');
                    setFeedbacks([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        getFeedbacks();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [clinicid, pagination.page, pagination.size]);

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({...prev, page: page - 1}));
    };

    const handleSizeChange = (e) => {
        setPagination(prev => ({...prev, size: Number(e.target.value), page: 0}));
    };

    const copyToClipboard = (text, itemType, itemId) => {
        navigator.clipboard.writeText(text).then(() => {
            // Set the copied item with a unique key
            const key = `${itemType}-${itemId}`;
            setCopiedItems(prev => ({...prev, [key]: true}));

            // Reset after 2 seconds
            setTimeout(() => {
                setCopiedItems(prev => ({...prev, [key]: false}));
            }, 2000);
        });
    };

    const getFeedbackBadge = (feedbackValue) => {
        switch (feedbackValue) {
            case 2: // Positive
                return <div className="badge bg-success bg-opacity-10 text-success px-2 py-1 small">
                    <i className="bi bi-emoji-smile text-success"></i> Positive
                </div>;
            case 1: // Neutral
                return <div className="badge bg-warning bg-opacity-10 text-warning px-2 py-1 small">
                    <i className="bi bi-emoji-neutral text-warning"></i> Neutral
                </div>;
            case 0: // Negative
                return <div className="badge bg-danger bg-opacity-10 text-danger px-2 py-1 small">
                    <i className="bi bi-emoji-frown text-danger"></i> Negative
                </div>;
            default:
                return <div className="badge bg-secondary px-2 py-1 small">Unknown</div>;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Generate pagination items
    const renderPaginationItems = () => {
        const items = [];
        const totalPages = pagination.totalPages;
        const currentPage = pagination.page + 1;

        // Always show first page
        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage}
                onClick={() => handlePageChange(1)}
                className="small"
            >
                1
            </Pagination.Item>
        );

        // Calculate start and end of page range to show
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add ellipsis if needed after first page
        if (startPage > 2) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" className="small"/>);
        }

        // Add middle pages
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                    className="small"
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Add ellipsis if needed before last page
        if (endPage < totalPages - 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" className="small"/>);
        }

        // Always show last page if there is more than one page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                    className="small"
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <div className="container-fluid py-4">
            <div className="shadow-none">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="card-title mb-0 fw-semibold text-primary">
                            <i className="bi bi-star me-2"></i>
                            Patient Feedbacks
                        </h5>
                        <div className="d-flex align-items-center">
                            <span className="me-2 small text-muted">Show:</span>
                            <select
                                className="form-select form-select-sm w-auto small"
                                value={pagination.size}
                                onChange={handleSizeChange}
                            >
                                {[5, 10, 20, 50].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {isLoading ? (
                        <Loading/>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table striped bordered hover className="mb-0 small">
                                    <thead className="table-light">
                                    <tr>
                                        <th width="40%"><i className="bi bi-person"></i> Patient</th>
                                        <th width="20%"><i className="bi bi-star"></i> Feedback</th>
                                        <th width="25%"><i className="bi bi-calendar"></i> Date</th>
                                        <th width="15%"><i className="bi bi-gear"></i> Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {feedbacks.length > 0 ? (
                                        feedbacks.map(feedback => (
                                            <React.Fragment key={feedback.id}>
                                                <tr>
                                                    <td>
                                                        <div className="fw-semibold">
                                                            {feedback.patientName}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {getFeedbackBadge(feedback.feedback)}
                                                    </td>
                                                    <td>
                                                        <div>{formatDate(feedback.createdAt)}</div>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className={`btn btn-sm ${expandedRows.includes(feedback.id) ? 'btn-outline-primary' : 'btn-outline-primary'}`}
                                                            onClick={() => toggleRow(feedback.id)}
                                                            title={expandedRows.includes(feedback.id) ? 'Collapse' : 'Expand'}
                                                        >
                                                            <i className={`bi ${expandedRows.includes(feedback.id) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedRows.includes(feedback.id) && (
                                                    <tr>
                                                        <td colSpan="4" className="p-0 border-top-0">
                                                            <Collapse in={expandedRows.includes(feedback.id)}>
                                                                <div className="p-3 bg-light">
                                                                    <h6 className="fw-semibold mb-3">Feedback
                                                                        Details</h6>
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Feedback
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{feedback.id}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(feedback.id, 'feedback', feedback.id)}
                                                                                    title="Copy Feedback ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`feedback-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Patient
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{feedback.patientId}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(feedback.patientId, 'patient', feedback.id)}
                                                                                    title="Copy Patient ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`patient-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                            <div className="mb-2">
                                                                                <small className="text-muted d-block">Patient
                                                                                    Name:</small>
                                                                                <span
                                                                                    className="font-monospace small">{feedback.patientName}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div
                                                                                className="mb-2 d-flex align-items-center">
                                                                                <small
                                                                                    className="text-muted d-block me-2">Clinic
                                                                                    ID:</small>
                                                                                <span
                                                                                    className="font-monospace small me-2">{feedback.clinicId}</span>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-secondary p-1"
                                                                                    onClick={() => copyToClipboard(feedback.clinicId, 'clinic', feedback.id)}
                                                                                    title="Copy Clinic ID"
                                                                                >
                                                                                    <i className={`bi ${copiedItems[`clinic-${feedback.id}`] ? 'bi-check text-success' : 'bi-copy'}`}></i>
                                                                                </button>
                                                                            </div>
                                                                            <div className="mb-2">
                                                                                <small className="text-muted d-block">Created
                                                                                    At:</small>
                                                                                <span
                                                                                    className="font-monospace small">{formatDate(feedback.createdAt)}</span>
                                                                            </div>
                                                                            <div className="mb-2">
                                                                                <small className="text-muted d-block">Feedback
                                                                                    Type:</small>
                                                                                <span className="fw-semibold">
                                                                                    {feedback.feedback === 2 ? 'Positive' :
                                                                                        feedback.feedback === 1 ? 'Neutral' : 'Negative'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4">
                                                <Alert variant="info" className="mb-0 small">
                                                    <i className="bi bi-info-circle me-2"></i>
                                                    {error || "No feedbacks found."}
                                                </Alert>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </Table>
                            </div>

                            {feedbacks.length > 0 && pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted small">
                                        Showing {Math.min(pagination.size, pagination.totalElements - (pagination.page * pagination.size))} of {pagination.totalElements} feedbacks
                                    </div>

                                    <Pagination className="mb-0 small">
                                        <Pagination.First
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(1)}
                                            className="small"
                                        />
                                        <Pagination.Prev
                                            disabled={pagination.page === 0}
                                            onClick={() => handlePageChange(pagination.page)}
                                            className="small"
                                        />

                                        {renderPaginationItems()}

                                        <Pagination.Next
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.page + 2)}
                                            className="small"
                                        />
                                        <Pagination.Last
                                            disabled={pagination.page >= pagination.totalPages - 1}
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                            className="small"
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FeedbackTable;